import {
  Element,
  Component,
  Event,
  EventEmitter,
  Host,
  h,
  Prop,
  State,
  Watch,
  Listen,
} from "@stencil/core";
import { searchApps } from "../../api/repository";
import { App } from "../../api/types";
import { parseCsvSlugs } from "../../lib/propParsing";
import {
  PlusAppIcon,
  MagnifyingGlassIcon,
  ClearButtonIcon,
} from "../../lib/icons";

const NULL_SELECTION = -1;

@Component({
  tag: "zapier-app-select",
  styleUrl: "zapier-app-select.css",
  shadow: true,
})
export class ZapierAppSelect {
  @Element() element!: HTMLElement;

  /** Emitted when an app is selected */
  @Event() appSelected!: EventEmitter<App | undefined>;

  /**
   * [TESTING] The ID of an HTML script tag with content-type=application/json
   * to load a fixed set of apps
   */
  @Prop() fromJson = "";

  /** If a comma separated list of strings is passed, hide them from the display */
  @Prop() hide = "";

  /** The first app being selected */
  @Prop() firstApp?: App;

  /** The second app being selected */
  @Prop() secondApp?: App;

  @State() private suggestionData?: App[];
  @State() private searching = false;
  @State() private searchAbortController?: AbortController;
  /** The selected app index */
  @State() private selection = NULL_SELECTION;
  /** The search query */
  @State() private query = "";
  /** If true, force an expanded display state */
  @State() private expanded = false;
  /** If false, show the clear button */
  @State() private hideClearButton = true;

  private input?: HTMLInputElement;
  private comboxWrapper?: HTMLDivElement;

  componentWillLoad() {
    if (this.fromJson && this.fromJson !== "") {
      const element = document.getElementById(this.fromJson);
      if (element) {
        if (element.getAttribute("type") !== "application/json") {
          throw new Error(`#${this.fromJson} is not application/json`);
        }
        this.suggestionData = JSON.parse(element.textContent ?? "[]");
      }
    }
  }

  @Listen("click", { target: "window" })
  checkForClickOutside(event: Event) {
    // When using Shadow DOM, you have to use `composedPath` to find the real
    // event target, rather than just the element hosting the shadow root
    //
    // https://developer.mozilla.org/en-US/docs/Web/API/Event/composedPath
    const realTarget = event.composedPath()[0] as HTMLElement;
    if (this.comboxWrapper?.contains(realTarget)) {
      return;
    }
    this.close();
  }

  private get hiddenSlugs() {
    return parseCsvSlugs(this.hide);
  }

  @Watch("query")
  async handleQuery() {
    const abortController = new AbortController();
    try {
      this.selection = 0;
      if (this.query.length === 0) {
        this.searching = false;
        return;
      }
      this.searching = true;
      // Abort the previous request so we don't have multiple requests to the
      // search API at the same time
      if (this.searchAbortController) {
        this.searchAbortController.abort();
      }
      this.searchAbortController = abortController;
      const data = await searchApps(this.query, {
        hideSlugs: this.hiddenSlugs,
        abortSignal: abortController.signal,
      });
      if (!data) {
        return;
      }
      this.suggestionData = data.results;
    } catch (error) {
      if (!abortController.signal.aborted) {
        throw error;
      }
    } finally {
      this.searching = false;
    }
  }

  private _handleInput = (event: Event) => {
    this.expanded = true;
    const element = event.target as HTMLInputElement;
    this.query = element.value;
  };

  private _handleKeyDown = (event: KeyboardEvent) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        this.setMenuKeyboardSelection(this.selection + 1);
        break;
      case "ArrowUp":
        event.preventDefault();
        this.setMenuKeyboardSelection(this.selection - 1);
        break;
      case "Enter":
        event.preventDefault();
        this.selectAppByIndex(this.selection);
        break;
      case "Escape":
        event.preventDefault();
        this.close();
        break;
    }
  };

  private handleFocus = () => {
    this.expanded = true;
  };

  private clear = () => {
    this.selection = NULL_SELECTION;
    this.suggestionData = undefined;
    this.hideClearButton = true;
    this.query = "";
    this.appSelected.emit(undefined);
    if (this.input) {
      this.input.focus();
    }
  };

  private close() {
    this.expanded = false;
    this.selection = 0;
    this.query = this.secondApp?.name || "";
    if (this.input) {
      this.input.setAttribute("aria-activedescendant", "");
    }
  }

  private setMenuKeyboardSelection(index: number) {
    if (this.suggestionData) {
      this.selection = Math.min(
        Math.max(index, 0),
        this.suggestionData.length - 1,
      );
    } else {
      this.selection = 0;
    }
    if (this.input) {
      this.input.setAttribute(
        "aria-activedescendant",
        "results-app-" + this.selection,
      );
    }
  }

  private selectAppByIndex(index: number) {
    this.setMenuKeyboardSelection(index);
    const app = this.suggestionData?.[index];
    this.appSelected.emit(app);
    this.query = app?.name || "";
    this.expanded = false;
    this.hideClearButton = false;
  }

  render() {
    const appIcon = this.secondApp?.images ? (
      <span class="icon">
        <img src={this.secondApp?.images.url_32x32} alt="" />
      </span>
    ) : (
      <MagnifyingGlassIcon />
    );
    return (
      <Host>
        <div class="app-select-host-container">
          <div class="app-select-container">
            <h3 class="connect-this">Connect this app...</h3>
            {this.firstApp ? (
              <div class="main-app icon-container">
                <span class="icon bg">
                  <img src={this.firstApp?.images?.url_64x64} alt="" />
                </span>
                <span class="app-name">{this.firstApp?.name}</span>
              </div>
            ) : (
              <span class="main-app icon-container" />
            )}
            <span class="plus-app">
              <PlusAppIcon />
            </span>
            <h3 class="with-this">with this one!</h3>
            <div
              class="combobox-wrapper"
              ref={(element) => {
                this.comboxWrapper = element;
              }}
            >
              <div
                class="inner search"
                aria-controls="search-listbox"
                aria-expanded={this.expanded ? "true" : "false"}
                aria-haspopup="listbox"
                aria-owns="search-listbox"
                role="combobox"
              >
                <div class="left icon-container" role="presentation">
                  <span aria-hidden="true" class="icon">
                    {appIcon}
                  </span>
                </div>
                <input
                  class="search"
                  aria-activedescendant
                  aria-autocomplete="list"
                  aria-label="Type to search for an app to connect"
                  autocomplete="off"
                  placeholder="Search for thousands of apps"
                  type="text"
                  value={this.query}
                  onFocus={this.handleFocus}
                  onInput={this._handleInput}
                  onKeyDown={this._handleKeyDown}
                  ref={(input) => {
                    if (!input) {
                      return;
                    }
                    this.input = input;
                  }}
                />
                <div class="right icon-container">
                  <div class="spinner" hidden={!this.searching} />
                  <button
                    hidden={this.hideClearButton}
                    type="button"
                    title="Clear selection"
                    onClick={this.clear}
                  >
                    <span class="icon close">
                      <ClearButtonIcon />
                    </span>
                  </button>
                </div>
              </div>
              {this.suggestionData && this.query && (
                <ul
                  aria-label="Apps"
                  class="results"
                  hidden={!this.expanded}
                  id="search-listbox"
                  role="listbox"
                >
                  {this.suggestionData.length === 0 ? (
                    <li
                      aria-selected="true"
                      class="no-results"
                      id="results-app-0"
                      role="option"
                    >
                      No search results found.
                    </li>
                  ) : (
                    this.suggestionData.map((app, i) => (
                      <li
                        aria-selected={this.selection === i ? "true" : "false"}
                        data-slug={app.slug}
                        class={{
                          "results-app": true,
                          highlight: this.selection === i,
                        }}
                        id={`results-app-${i}`}
                        role="option"
                        onMouseEnter={(event) => {
                          event.preventDefault();
                          this.setMenuKeyboardSelection(i);
                        }}
                        onClick={(event) => {
                          event.preventDefault();
                          this.selectAppByIndex(i);
                        }}
                      >
                        <span class="icon" aria-hidden="true">
                          <img src={app.images.url_32x32} alt="" />
                        </span>
                        <span class="text">{app.name}</span>
                      </li>
                    ))
                  )}
                </ul>
              )}
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
